import { FunctionComponent } from 'react'
import { SiteRFPSummary } from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Chip,
  Link,
  TableCell,
  TableRow,
  rucksack,
} from '@black-bear-energy/rucksack'
import { ActualOrEstimatedDate } from '../common/ActualOrEstimatedDate'
import { ArrayLocationChip } from '../common/ArrayLocationChip'
import { TableCellWithDefault } from '../common/TableCellWithDefault'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { SiteRFPPhaseChip } from '../common/SiteRFPPhaseChip'
import { handlePhase } from 'components/helpers/handlePhase'
import { formatSignificantFigures } from '../helpers/formatSignificantFigures'

const PHASE_CHIP_HEIGHT = 24
const PHASE_CHIP_GAP = 4
// Reserve enough height for two stacked chips so rows with a single phase chip
// align with DeriskingAndPermits rows, which split into two chips.
const PHASE_CELL_MIN_HEIGHT = PHASE_CHIP_HEIGHT * 2 + PHASE_CHIP_GAP

export const SitesTableSection: FunctionComponent<SitesTableSectionProps> = ({
  record,
  search,
}) => {
  const systemSize =
    record.systemSize !== null
      ? formatSignificantFigures(record.systemSize) + ' MW'
      : ''
  const sitePath = `/sites/${record.siteRFPId}`

  return (
    <TableRow
      tabIndex={0}
      sx={{
        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        padding: '4px 0px',
        '&:hover': { backgroundColor: rucksack.palette.action.hover },
        '&:focus': { backgroundColor: rucksack.palette.action.selected },
        '& .MuiTableCell-root': { padding: '4px 0px 4px 16px' },
      }}
    >
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Link
              href={sitePath}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{
                // Override the pink hover and focus color defined in index.css.
                '&:hover, &:focus': {
                  color: rucksack.palette.primary.main,
                },
              }}
            >
              <TextWithTooltip
                text={record.siteRFPName}
                maxLineCount={1}
                searchString={search}
              />
            </Link>
            {record.section && (
              <Chip
                label={record.section}
                size="mini"
                sx={{
                  bgcolor: rucksack.palette.action.selected,
                }}
              />
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <TextWithTooltip
          text={record.customerRFPName}
          variant="body2"
          maxLineCount={2}
          searchString={search}
        />
      </TableCell>
      <TableCellWithDefault>{systemSize}</TableCellWithDefault>
      <TableCellWithDefault>
        {record.arrayLocation && (
          <ArrayLocationChip arrayLocation={record.arrayLocation} />
        )}
      </TableCellWithDefault>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: `${PHASE_CHIP_GAP}px`,
            minHeight: PHASE_CELL_MIN_HEIGHT,
            justifyContent: 'center',
          }}
        >
          {handlePhase(record.phase).map((chip) => (
            <SiteRFPPhaseChip key={chip} phase={chip} scale="small" />
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <ActualOrEstimatedDate
          actualDate={record.constructionStartDate}
          estimatedDate={record.constructionStartDateEstimate}
        />
      </TableCell>
      <TableCell>
        <ActualOrEstimatedDate
          actualDate={record.commercialOperationDate}
          estimatedDate={record.commercialOperationDateEstimate}
        />
      </TableCell>
    </TableRow>
  )
}

SitesTableSection.displayName = 'SitesTableSection'

interface SitesTableSectionProps {
  record: SiteRFPSummary
  search?: string
}
